/*
  How the Document settings modal (host) communicates with the module in the active section.

  Every module owns its own RHF form, but the sections, Close, X, footer Save and the unsaved-changes
  modal live outside that form, so they cannot read its `formState`, call its `reset()` or submit
  it. The module hands those three things over through `useRegisterInvoicingSettingsForm`.
  `useRegisterInvoicingSettingsPreviewPatch` separately reports the unsaved values that affect the
  preview. The context makes the active form's handlers and preview values available to the modal.

  What each value is for:
  - `visibleSections` — the sections the user can reach, from the entry context and the user's
    scopes. Navigation to a section outside this list is ignored.
  - `activeSection` — which module is rendered; switching sections unmounts the previous one. It is
    derived: when the selected section is not in `visibleSections`, it falls back to the first visible one.
  - `isDirty` — the dirty guard. Only one module can be dirty, because only one is mounted.
  - `requestNavigation` — the one entry point for switching sections and closing (side nav, X,
    Escape). Ignored while saving; when the form is dirty, the navigation is remembered as
    `pendingNavigation` and the unsaved-changes modal asks the user what to do. That modal then
    calls `applyPendingNavigation` or `cancelPendingNavigation`.
  - `registerFormReset` — after a successful Save, the still-mounted form is told to treat the saved
    values as its new baseline. Discard does not need it: it always unmounts the form.
  - `registerFormSave` — the save of whichever form is mounted. Only the form knows which endpoints
    its fields belong to, so it registers the whole save and the host just waits for it. One Save
    may call several endpoints (settings document, reminders, templates).
  - `save` / `isProcessing` — runs the registered save. Footer, dirty guard and the unsaved-changes
    modal share this one `isProcessing`, so none of them can miss an in-flight save. When the form
    reports 'invalid', the host drops the pending navigation and closes the unsaved-changes modal so
    the field errors are visible; nothing is reported and the form stays dirty.
  - `previewPatch` / `setPreviewPatch` — the mounted form's unsaved values that affect the preview.
    Unmount clears them. Save does not read them, and updating them does not call the writer.

  Leaving the page (a pathname change, or the host unmounting with its route) closes both the
  settings modal and the unsaved-changes modal and drops any pending navigation, without going
  through `requestNavigation`. See `useCloseInvoicingSettingsOnLeave`.
*/
import { notify, useModal } from '@billy/design-system'
import { trackError } from '@billy/track-error'
import {
  createContext,
  type Dispatch,
  type PropsWithChildren,
  type ReactElement,
  type SetStateAction,
  useCallback,
  useContext,
  useMemo,
  useRef,
  useState,
} from 'react'
import { useTranslation } from 'react-i18next'
import { useMutation } from 'react-query'

import { ModalId } from '../../../enums/modalId'
import { NotificationKeys } from '../../../enums/notificationKeys'
import type { InvoicingSettingsEntryContext } from '../enums/invoicingSettingsEntryContext'
import { InvoicingSettingsSection } from '../enums/invoicingSettingsSection'
import { useCloseInvoicingSettingsOnLeave } from '../hooks/useCloseInvoicingSettingsOnLeave'
import { useVisibleInvoicingSettingsSections } from '../hooks/useVisibleInvoicingSettingsSections'
import { useZervantSettings } from '../hooks/useZervantSettings'
import type {
  InvoicingSettingsFormSave,
  InvoicingSettingsFormSaveOptions,
  InvoicingSettingsFormSaveResult,
} from '../types/invoicingSettingsFormSave'
import type { InvoicingSettingsPreviewPatch } from '../types/invoicingSettingsPreviewPatch'
import { useUpdateInvoicingSettings } from '../writer'

type InvoicingSettingsPendingNavigation = { section: InvoicingSettingsSection; type: 'section' } | { type: 'close' }

interface ContextState {
  activeSection: InvoicingSettingsSection
  applyPendingNavigation: () => void
  cancelPendingNavigation: () => void
  isDirty: boolean
  isProcessing: boolean
  previewPatch: InvoicingSettingsPreviewPatch | undefined
  registerFormReset: (resetForm: () => void) => () => void
  registerFormSave: (save: InvoicingSettingsFormSave) => () => void
  requestNavigation: (navigation: InvoicingSettingsPendingNavigation) => void
  save: (onSaved?: () => void) => void
  setIsDirty: Dispatch<SetStateAction<boolean>>
  setPreviewPatch: Dispatch<SetStateAction<InvoicingSettingsPreviewPatch | undefined>>
  visibleSections: InvoicingSettingsSection[]
}

const InvoicingSettingsHostContext = createContext<ContextState | undefined>(undefined)

interface InvoicingSettingsHostContextProviderProps extends PropsWithChildren {
  entryContext?: InvoicingSettingsEntryContext
  initialSection?: InvoicingSettingsSection
  modalId: string
}

export const InvoicingSettingsHostContextProvider = ({
  children,
  entryContext,
  initialSection = InvoicingSettingsSection.LogoAndBranding,
  modalId,
}: InvoicingSettingsHostContextProviderProps): ReactElement => {
  const { t } = useTranslation()
  const visibleSections = useVisibleInvoicingSettingsSections(entryContext)
  const { close: closeSettingsModal } = useModal(modalId)
  const { close: closeUnsavedChangesModal, open: openUnsavedChangesModal } = useModal(
    ModalId.InvoicingSettingsUnsavedChangesModal,
  )
  const [selectedSection, setSelectedSection] = useState(initialSection)
  const activeSection = visibleSections.includes(selectedSection) ? selectedSection : visibleSections[0]
  const [isDirty, setIsDirty] = useState(false)
  const [pendingNavigation, setPendingNavigation] = useState<InvoicingSettingsPendingNavigation>()
  const [previewPatch, setPreviewPatch] = useState<InvoicingSettingsPreviewPatch>()
  const formResetRef = useRef<() => void>()
  const formSaveRef = useRef<InvoicingSettingsFormSave>()

  // Populate the zervant-settings query cache; the writer reads it at save time.
  useZervantSettings()

  const { update: updateSettings } = useUpdateInvoicingSettings()

  // The host does not know the form's fields, so the mounted form registers its reset handler here.
  // Cleanup unregisters only if this form is still the registered one.
  const registerFormReset = useCallback((resetForm: () => void) => {
    formResetRef.current = resetForm

    return () => {
      if (formResetRef.current === resetForm) {
        formResetRef.current = undefined
      }
    }
  }, [])

  // The host does not know which endpoints the form owns, so the mounted form registers one save
  // handler here. Cleanup unregisters only if this form is still the registered one.
  const registerFormSave = useCallback((formSave: InvoicingSettingsFormSave) => {
    formSaveRef.current = formSave

    return () => {
      if (formSaveRef.current === formSave) {
        formSaveRef.current = undefined
      }
    }
  }, [])

  const runRegisteredFormSave = useCallback(
    async (options: InvoicingSettingsFormSaveOptions): Promise<InvoicingSettingsFormSaveResult> =>
      formSaveRef.current ? formSaveRef.current(options) : 'saved',
    [],
  )

  // One mutation for the whole Save, however many requests the form makes inside it.
  const { isLoading: isProcessing, mutate: runActiveFormSave } = useMutation(runRegisteredFormSave, {
    onError: (error: unknown) => {
      trackError(error)
      notify({
        id: NotificationKeys.InvoicingSettingsSave,
        message: t('changes_save_error'),
        variant: 'error',
      })
    },
    onSuccess: (result) => {
      if (result === 'invalid') {
        setPendingNavigation(undefined)
        closeUnsavedChangesModal()
        return
      }

      setIsDirty(false)
      formResetRef.current?.()
      notify({
        id: NotificationKeys.InvoicingSettingsSave,
        message: t('changes_saved'),
        variant: 'success',
      })
    },
  })

  const save = useCallback(
    (onSaved?: () => void) => {
      if (isProcessing) {
        return
      }

      runActiveFormSave(
        { updateSettings },
        {
          onSuccess: (result) => {
            if (result === 'saved') {
              onSaved?.()
            }
          },
        },
      )
    },
    [isProcessing, runActiveFormSave, updateSettings],
  )

  const applyNavigation = useCallback(
    (navigation: InvoicingSettingsPendingNavigation) => {
      if (navigation.type === 'section') {
        setSelectedSection(navigation.section)
        return
      }

      closeSettingsModal()
    },
    [closeSettingsModal],
  )

  const requestNavigation = useCallback(
    (navigation: InvoicingSettingsPendingNavigation) => {
      if (
        isProcessing ||
        (navigation.type === 'section' &&
          (navigation.section === activeSection || !visibleSections.includes(navigation.section)))
      ) {
        return
      }

      if (isDirty) {
        setPendingNavigation(navigation)
        openUnsavedChangesModal()
        return
      }

      applyNavigation(navigation)
    },
    [activeSection, applyNavigation, isDirty, isProcessing, openUnsavedChangesModal, visibleSections],
  )

  const applyPendingNavigation = useCallback(() => {
    if (pendingNavigation) {
      applyNavigation(pendingNavigation)
    }

    setPendingNavigation(undefined)
  }, [applyNavigation, pendingNavigation])

  const cancelPendingNavigation = useCallback(() => {
    setPendingNavigation(undefined)
  }, [])

  useCloseInvoicingSettingsOnLeave({
    closeSettingsModal,
    closeUnsavedChangesModal,
    resetPendingNavigation: cancelPendingNavigation,
  })

  const contextValue = useMemo(
    () => ({
      activeSection,
      applyPendingNavigation,
      cancelPendingNavigation,
      isDirty,
      isProcessing,
      previewPatch,
      registerFormReset,
      registerFormSave,
      requestNavigation,
      save,
      setIsDirty,
      setPreviewPatch,
      visibleSections,
    }),
    [
      activeSection,
      applyPendingNavigation,
      cancelPendingNavigation,
      isDirty,
      isProcessing,
      previewPatch,
      registerFormReset,
      registerFormSave,
      requestNavigation,
      save,
      setPreviewPatch,
      visibleSections,
    ],
  )

  return <InvoicingSettingsHostContext.Provider value={contextValue}>{children}</InvoicingSettingsHostContext.Provider>
}

export const useInvoicingSettingsHost = () => {
  const context = useContext(InvoicingSettingsHostContext)

  if (!context) {
    throw new Error('InvoicingSettingsHostContextProvider is missing in the module!')
  }

  return context
}
